import { useEffect, useState } from 'react';
import Select, { components } from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { IAddress } from '@interfaces';
import { STATES, STATES_MAP } from '@components/common/helpers/state-helper';
import { useFormCallbacks, useInitForm } from '@v2/hooks/form';
import { useFormState } from '@app/store/rtk.form.selectors';
import Loader from '@v2/components/common/presentational/loader/loader';
import { validateAddress, validateCity, validateRequired } from '@v2/components/common/helpers/field-validation';
import { emptyForm } from '@v2/store/rtk.form.store';
import { useDispatch } from 'react-redux';

export default function AddressModal(
    { address: addressDefault, entity, title, hide, callback }:
    { address: IAddress, entity: string, title: string, hide: Function, callback: Function }
) {
    const dispatch = useDispatch();
    useInitForm('address', addressDefault, 100);
    const allStates = STATES;
    const [loading, setLoading] = useState(false);
    const { updateTextInput, updateValue } = useFormCallbacks('address');

    const data = useFormState('address') || addressDefault;
    const { id, nickname, firstName, lastName, address1, address2,
        city, state, postcode } = data;

    const { register, handleSubmit, formState: {errors}, control } = useForm({ reValidateMode: 'onBlur', values: Object.assign({}, data) });

    useEffect(() => {
        return () => {
            dispatch(emptyForm('address'));
        }
    }, []);

    function onSubmit() {
        setLoading(true);

        callback(data, entity, id).finally(() => {
            setLoading(false);
            hide();
        });
    }

    const handleInput = (e: React.FormEvent<EventTarget>, formKey: string) => {
        let hasValue = false;

        try {
            hasValue = (e.target as HTMLInputElement).matches(':autofill');
        } catch (err) {
            try {
                hasValue = (e.target as HTMLInputElement).matches(':-webkit-autofill');
            } catch (er) {
                console.error(er);
                hasValue = false;
            }
        }

        if (hasValue) {
            updateTextInput(formKey)(e);
        }
    }

    async function handleChangeSelect(valueObject: any) {
        if (!valueObject || !valueObject.value) {
            return false;
        }
        updateValue({state: valueObject })
    }

     // @ts-ignore
    const Input = ({ ...rest }) => <components.Input
        {...rest}
        autoComplete={'address-level1'}
        onChange={(event: any) => {
            if (STATES_MAP[event.target.value]) {
                handleChangeSelect(STATES_MAP[event.target.value]);
            } else {
                rest.onChange(event);
            }
        }} />

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <h2>{title}</h2>
            <br />

            <div className="row">
                <div className="col">
                    <input type="hidden" name="id" value={id} />
                    <label className={errors.nickname ? 'error' : null}>
                        Nickname
                        <input
                            type="text"
                            name="nickname"
                            aria-label={'nickname'}
                            onChange={updateTextInput('nickname')}
                            value={nickname || ''}
                            placeholder="Enter nickname" />
                    </label>
                </div>
            </div>

            <div className="row">
                <div className="col-6">
                    <label className={errors.firstName ? 'error' : null}>
                        First Name
                        <input
                            type="text"
                            name="firstName"
                            aria-label={'first name'}
                            aria-required="true"
                            value={firstName}
                            placeholder="First name"
                            autoComplete={'given-name'}
                            onInput={(e: any) => handleInput(e, 'firstName')}
                            {...register('firstName', {
                                onChange: updateTextInput('firstName'),
                                validate: {
                                    value: (value: string) => value && value.trim() !== ''
                                        ? true
                                        : 'This field is required'
                                }
                            })} />
                        <div className="error-message">
                            <ErrorMessage errors={errors} name="firstName" />
                        </div>
                    </label>
                </div>
                <div className="col-6">
                    <label className={errors.lastName ? 'error' : null}>
                        Last Name
                        <input
                            type="text"
                            name="lastName"
                            aria-label={'last name'}
                            aria-required="true"
                            value={lastName}
                            placeholder="Last name"
                            autoComplete={'family-name'}
                            onInput={(e: any) => handleInput(e, 'lastName')}
                            {...register('lastName', {
                                onChange: updateTextInput('lastName'),
                                validate: {
                                    value: (value: string) => value && value.trim() !== ''
                                        ? true
                                        : 'This field is required'
                                }
                            })} />
                        <div className="error-message">
                            <ErrorMessage errors={errors} name="lastName" />
                        </div>
                    </label>
                </div>
            </div>


            <label className={errors.address1 ? 'error' : null}>
                Address
                <input
                    type="text"
                    name="address1"
                    aria-label={'address'}
                    aria-required="true"
                    value={address1}
                    placeholder="Address line 1"
                    autoComplete={'address-line1'}
                    onInput={(e: any) => handleInput(e, 'address1')}
                    {...register('address1', {
                        onChange: updateTextInput('address1'),
                        validate: {
                            required: validateRequired,
                            address: validateAddress,
                        }
                    })} />
                <div className="error-message">
                    <ErrorMessage errors={errors} name="address1" />
                </div>
            </label>
            <label className={errors.address2 ? 'error' : null}>
                Address 2
                <input
                    type="text"
                    name="address2"
                    aria-label={'address line 2'}
                    autoComplete={'address-line2'}
                    onInput={(e: any) => handleInput(e, 'address2')}
                    onChange={updateTextInput('address2')}
                    value={address2 || ''}
                    placeholder="Address line 2" />
            </label>
            <label className={errors.city ? 'error' : null}>
                City
                <input
                    type="text"
                    name="city"
                    aria-label={'city'}
                    aria-required="true"
                    value={city}
                    placeholder="Enter city"
                    autoComplete={'address-level2'}
                    onInput={(e: any) => handleInput(e, 'city')}
                    {...register('city', {
                        onChange: updateTextInput('city'),
                        validate: {
                            required: validateRequired,
                            city: validateCity,
                        }
                    })} />
                <div className="error-message">
                    <ErrorMessage errors={errors} name="city" />
                </div>
            </label>

            <div className="row">
                <div className="col-7">
                    <label className={errors.state ? 'error' : null}>
                        State
                        <Controller
                            control={control}
                            name="state"
                            aria-label={'state'}
                            aria-required="true"
                            rules={{required: 'This field is required'}}
                            defaultValue={state}
                            render={({ field: { onChange, onBlur }}) => (
                                <Select
                                    components={{ Input }}
                                    options={allStates}
                                    value={allStates.find(obj => obj.value === state || obj.value === state?.value)}
                                    onBlur={onBlur}
                                    onChange={v => {
                                        handleChangeSelect(v);
                                        onChange(v);
                                    }}
                                    className="select-dropdown state-dropdown"
                                    classNamePrefix="select" />
                            )} />
                        <div className="error-message">
                            <ErrorMessage errors={errors} name="state" />
                        </div>
                    </label>
                </div>
                <div className="col-5">
                    <label className={errors.postcode ? 'error' : null}>
                        ZIP Code
                        <input
                            type="text"
                            name="postcode"
                            aria-label={'zipcode'}
                            aria-required="true"
                            value={postcode}
                            autoComplete={'postal-code'}
                            placeholder="Enter zip code"
                            onInput={(e: any) => handleInput(e, 'postcode')}
                            {...register('postcode', {
                                onChange: updateTextInput('postcode'),
                                required: 'This field is required',
                                validate: {
                                    value: (value: string) => /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value)
                                                                ? true
                                                                : 'Please provide a valid ZIP code'
                                }
                            })}/>
                            <div className="error-message">
                                <ErrorMessage errors={errors} name="postcode" />
                            </div>
                    </label>
                </div>
            </div>
            <button className="button primary blue width100">Save</button>
            {loading && <Loader overlay={true} />}
        </form>
    );
}
